import {
  defaultLoadableState,
  LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useContext, useMemo } from 'react';
import OrderState from '~/constants/home/orderState';
import UserPurchaseData from '~/services/common/contracts/UserPurchaseData';
import { DeepPartial } from '~/types';

type UserPurchaseContextType = {
  userPurchase: LoadableState<UserPurchaseData>;
};

export const defaultUserPurchaseContext: UserPurchaseContextType = {
  userPurchase: defaultLoadableState<UserPurchaseData>({
    orderState: OrderState.None
  })
};

const UserPurchaseContext = createContext<UserPurchaseContextType>(
  defaultUserPurchaseContext
);

export function UserPurchaseContextProvider({
  children
}: React.PropsWithChildren) {
  const userPurchase = useLoadableState<UserPurchaseData>({
    orderState: OrderState.None
  });

  const value = useMemo<UserPurchaseContextType>(
    () => ({
      userPurchase
    }),
    [userPurchase]
  );

  return (
    <UserPurchaseContext.Provider value={value}>
      {children}
    </UserPurchaseContext.Provider>
  );
}

UserPurchaseContextProvider.displayName = 'UserPurchaseContextProvider';

export const generateUserPurchaseContext = (
  customState: DeepPartial<UserPurchaseContextType>
): UserPurchaseContextType =>
  deepmerge<UserPurchaseContextType, DeepPartial<UserPurchaseContextType>>(
    defaultUserPurchaseContext,
    customState
  );

export function useGetUserPurchase() {
  return useContext(UserPurchaseContext).userPurchase;
}

export function useGetUserPurchaseIsLoaded() {
  return useContext(UserPurchaseContext).userPurchase.isLoaded;
}

export default UserPurchaseContext;
